import { ICON__scaling } from '@/assets/icons_lucide';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { PLACEHOLDER_BUTTON__bar_8_5 } from '@/pages/_base/buttons/PLACEHOLDER_BUTTON__bar_8_5';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { cn } from '@/utils/utils_client';
import { assert_array_includes } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ENUM__image_transform } from '@shared/dto/generation/graph_invoke/_fields/image/_enums/ENUM__image_transform';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';

interface PROPS {
  field: FIELD__image_asset;
}

export function G_DROPDOWN__field_image__transform({ field }: PROPS) {
  /*
   * STORE
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  const { preprocessing_transform__current } = field;

  /*
   * STATE
   */

  /*
   * EVENTS
   */
  async function on_item_selected(value: ENUM__image_transform) {
    if (value == preprocessing_transform__current) return;

    assert_array_includes(field.preprocessing_transform__allowed, value);

    let field_new = field.clone();
    field_new.preprocessing_transform__current = value;

    let graph_new = generation_current.tool.graph.clone();

    graph_new.field__replace(field_new);

    await UTILS__graph_data.update(graph_new);
  }

  function is_disabled() {
    return g_general__ui_disabled;
  }

  /*
   *
   */

  function render_item(value: ENUM__image_transform) {
    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={`field_store_interaction_${value}`}
        values_selected={[preprocessing_transform__current]}
        class_name_hovered_selected="bg-component-item-selected"
        className={cn('px-2 py-0.5 cursor-pointer text-xs bg-component text-component-item-foreground')}
        value={value}
        on_item_selected={() => void on_item_selected(value)}
      >
        {value}
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  /*
   * HTML
   */
  if (!field.get_has_content()) return <PLACEHOLDER_BUTTON__bar_8_5 />;
  if (field.preprocessing_transform__allowed.length <= 1) return <PLACEHOLDER_BUTTON__bar_8_5 />;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_BUTTON__bar_8_5 />;

  return (
    <>
      <UI_PRIMITIVE__dropdown_menu modal={false}>
        <UI_PRIMITIVE__dropdown_menu_trigger asChild>
          <UI_PRIMITIVE__button_with_icon_animated
            title={'Store between tools'}
            variant={'no-border-on-bar'}
            Icon={ICON__scaling}
            disabled={is_disabled()}
            onClick={() => {}}
            renderDelay={CONSTANTS_TIME__client.TIMEOUT_DELAY_BEFORE_FIRST_ANIMATION_RENDER}
            className={`w-8 h-5 flex-center ${preprocessing_transform__current == ENUM__image_transform.NONE ? 'opacity-50' : ''}`}
            iconClassNameBase="size-3 text-gray-400"
            iconClassNameHovered="!text-bar-foreground"
          />
        </UI_PRIMITIVE__dropdown_menu_trigger>

        <UI_PRIMITIVE__dropdown_menu_content
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg dark:bg-component dark:border-component-border p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          {Object.values(field.preprocessing_transform__allowed).map(render_item)}
        </UI_PRIMITIVE__dropdown_menu_content>
      </UI_PRIMITIVE__dropdown_menu>
    </>
  );
}
